import React, { useMemo } from 'react'
import { GridContainer, MappingSelector, TenantSelector } from './components'
import {
  useDragAndDrop,
  useMappingOperations,
  useTenantSelection,
  useThreeWayData,
} from './hooks'
import { MappingMutationParams, ThreeWayMappingViewProps } from './types'
import { columnGroupStyles, getColumnDefinitions } from './utils'

export const ThreeWayMappingView: React.FC<ThreeWayMappingViewProps> = ({
  entityType,
}) => {
  // Initialize tenant selection
  const tenantProps = useTenantSelection({ entityType })

  // Initialize drag and drop state
  const dragDropProps = useDragAndDrop()

  // Initialize mapping operations
  const { createMappingMutation } = useMappingOperations({ entityType })

  // Initialize data fetching and alignment
  const dataProps = useThreeWayData({
    entityType,
    leftTenant: tenantProps.leftTenant,
    rightTenant: tenantProps.rightTenant,
  })

  // Handle mapping creation
  const handleCreateMapping = (params: MappingMutationParams) => {
    createMappingMutation.mutate(params)
  }

  // Generate column definitions with all necessary props
  const columnDefs = useMemo(() => {
    return getColumnDefinitions({
      leftTenant: tenantProps.leftTenant,
      rightTenant: tenantProps.rightTenant,
      isDragging: dragDropProps.isDragging,
      draggedData: dragDropProps.draggedData,
      dragSource: dragDropProps.dragSource,
      alignedRows: dataProps.alignedRows,
      onDragStart: dragDropProps.handleDragStart,
      onDragEnd: dragDropProps.handleDragEnd,
      onCreateMapping: handleCreateMapping,
      entityType: entityType,
      MappingSelectorComponent: MappingSelector,
    })
  }, [
    tenantProps.leftTenant,
    tenantProps.rightTenant,
    dragDropProps.isDragging,
    dragDropProps.draggedData,
    dragDropProps.dragSource,
    dataProps.alignedRows,
    dragDropProps.handleDragStart,
    dragDropProps.handleDragEnd,
    handleCreateMapping,
    entityType,
  ])

  // Generate action buttons
  const actionButtons = (
    <TenantSelector
      leftTenant={tenantProps.leftTenant}
      rightTenant={tenantProps.rightTenant}
      tenants={tenantProps.tenants}
      onLeftTenantChange={tenantProps.handleLeftTenantChange}
      onRightTenantChange={tenantProps.handleRightTenantChange}
      onSwapTenants={tenantProps.handleSwapTenants}
    />
  )

  return (
    <GridContainer
      alignedRows={dataProps.alignedRows}
      columnDefs={columnDefs}
      isLoading={dataProps.isLoading}
      actionButtons={actionButtons}
      columnGroupStyles={columnGroupStyles}
    />
  )
}
